// Where each photo or video goes on the walk map. Phone uploads never carry a location (Android
// blanks it for browsers), but their taken time survives, and the route says where the walk was
// at that moment. A file that does carry its own location (copied to a computer first) uses it.

// Taken this long before the first fix or after the last still counts, pinned at that end
// (a photo at the gate before Start, or just after Save).
export const END_TOLERANCE_MS = 2 * 60 * 1000
// Across a longer gap between fixes (GPS lost, or paused) the walker could have been anywhere in
// between, so the photo goes to whichever fix is nearer in time rather than a point on the line.
export const MAX_INTERPOLATION_GAP_MS = 60 * 1000

// track: the walk page's /api/walks/:id/track reply (points with t seconds from start_ms).
// Returns { pins: [{ index, photo, lat, lon, from, d }], outside } where index is the photo's place
// in photos, from is 'file' or 'route', d is the distance along the route at the taken time (for
// the elevation chart; null without a time in the walk), and outside counts photos with a taken
// time but not during the walk. Photos with no time at all (MapMyWalk ones the Google Photos Takeout
// had no copy of, scripts/photo_match/takeout_times.py) are simply left off.
export function placePhotos(photos, track) {
  const points = track?.points ?? []
  const pins = []
  let outside = 0
  photos.forEach((photo, index) => {
    const at = photo.taken_at && points.length && track.start_ms
      ? positionAt(points, (Date.parse(photo.taken_at) - track.start_ms) / 1000)
      : null
    if (photo.latitude != null && photo.longitude != null) {
      // A shared page's photos come placed by the server, with their distance along the route (d).
      pins.push({ index, photo, lat: photo.latitude, lon: photo.longitude, from: 'file', d: at?.d ?? photo.d ?? null })
      return
    }
    if (at) pins.push({ index, photo, ...at, from: 'route' })
    else if (photo.taken_at && points.length && track.start_ms) outside++
  })
  return { pins, outside }
}

// The route's position ({lat, lon, d}) at t seconds from its start, or null if t is outside the
// walk.
export function positionAt(points, t) {
  const first = points[0]
  const last = points[points.length - 1]
  const tolerance = END_TOLERANCE_MS / 1000
  if (t < first.t - tolerance || t > last.t + tolerance) return null
  const at = (p) => ({ lat: p.lat, lon: p.lon, d: p.d })
  if (t <= first.t) return at(first)
  if (t >= last.t) return at(last)
  // Binary search for the last point at or before t.
  let lo = 0
  let hi = points.length - 1
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (points[mid].t <= t) lo = mid
    else hi = mid
  }
  const a = points[lo]
  const b = points[hi]
  const gap = b.t - a.t
  if (gap <= 0) return at(a)
  if (gap > MAX_INTERPOLATION_GAP_MS / 1000) return at(t - a.t <= b.t - t ? a : b)
  const k = (t - a.t) / gap
  return { lat: a.lat + k * (b.lat - a.lat), lon: a.lon + k * (b.lon - a.lon), d: a.d + k * (b.d - a.d) }
}

// Pins closer than this on screen overlap (they're 36 px across), so they're shown as one.
export const GROUP_RADIUS_PX = 30

// Groups pins that would overlap. project(pin) gives its screen position ({x, y}) at the zoom in
// question. Greedy, in photo order: each pin joins the first group whose first pin is within
// radius, else starts a group. Returns [{ pins, lat, lon }], each group placed at its first pin.
export function groupPins(pins, project, radius = GROUP_RADIUS_PX) {
  const groups = []
  for (const pin of pins) {
    const p = project(pin)
    const group = groups.find((g) => Math.hypot(g.x - p.x, g.y - p.y) < radius)
    if (group) group.pins.push(pin)
    else groups.push({ pins: [pin], lat: pin.lat, lon: pin.lon, x: p.x, y: p.y })
  }
  return groups.map(({ pins: members, lat, lon }) => ({ pins: members, lat, lon }))
}
